import React, { useState, useRef, useEffect } from 'react'
import { MdClose, MdKeyboardArrowDown } from 'react-icons/md'

export type TMultipleSelectOption = {
  readonly value: string
  readonly title: string
}

export type TMultipleSelect = {
  readonly options: TMultipleSelectOption[]
  readonly value: string[]
  readonly onChange: (value: string[]) => void
  readonly error?: boolean
  readonly errorText?: string
}

export default function MultipleSelect({
  options,
  value,
  onChange,
  error,
  errorText,
}: TMultipleSelect) {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  const handleSelect = (optionValue: string) => {
    const updatedValues = value.includes(optionValue)
      ? value.filter((val) => val !== optionValue)
      : [...value, optionValue]

    onChange(updatedValues)
  }

  const handleRemove = (optionValue: string) => {
    const updatedValues = value.filter((val) => val !== optionValue)
    onChange(updatedValues)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className='relative flex w-full flex-col gap-1' ref={selectRef}>
      <div className='sentry-mask relative'>
        <div
          className={`w-full rounded-lg border ${
            error ? 'border-pop-error' : 'border-pop-gray-5'
          } cursor-pointer px-3 py-4`}
          onClick={handleToggle}
          tabIndex={0}
        >
          <div className='flex items-center justify-between'>
            <div className='flex flex-wrap gap-2 text-sm font-light'>
              {value.length > 0 ? (
                value.map((val) => (
                  <div
                    key={val}
                    className='flex flex-row items-center justify-between gap-2 rounded-full bg-pop-gray-6 px-3 py-1'
                  >
                    <span className='text-xs font-light'>
                      {options.find((opt) => opt.value === val)?.title}
                    </span>
                    <div
                      className='cursor-pointer rounded-full hover:bg-pop-gray-4'
                      onClick={() => handleRemove(val)}
                    >
                      <MdClose width={20} height={20} className='h-5 w-5' />
                    </div>
                  </div>
                ))
              ) : (
                <span>Choose Answer</span>
              )}
            </div>
            <MdKeyboardArrowDown className='h-6 w-6 text-pop-grey-600' />
          </div>
        </div>
        {isOpen && (
          <div className='absolute z-10 mt-1 max-h-96 w-full overflow-y-auto rounded-md bg-white shadow-lg'>
            {options.map((option) => (
              <div
                key={option.value}
                className={`cursor-pointer px-4 py-2 text-sm font-light hover:bg-gray-200 ${
                  value.includes(option.value) ? 'bg-gray-200' : ''
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.title}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && <span className='text-xs text-pop-error'>{errorText}</span>}
    </div>
  )
}
